import { FEAT, formFx } from '@somnhold/content';
import { cellAtWorld, heroWalkable } from '../grid.ts';
import { dist2, idiv, imax, imin, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, UqDue } from '../types.ts';
import { damageEnemy, enemiesNear, ev, healHero, isFlying, targetable } from './combat.ts';
import { featPieceTest, featPieceWatch, featUse } from './feats.ts';
import { formFinal } from './forms.ts';
import { raiseShield } from './kits.ts';
import { along, enemiesInCone, shove } from './skills.ts';
import { stunFor } from './towers.ts';
import { rangerDue, rangerHeroTick } from './panoply-ranger.ts';
import { clericDue, clericHeroTick, clericHurt } from './panoply-cleric.ts';
import { fx, hasUq, heavyStun } from './uniques.ts';
import { druidDue } from './panoply-druid.ts';
import { barbarianDied, barbarianDue, barbarianHit } from './panoply-barbarian.ts';
import { paladinDied, paladinDue, paladinHit, paladinTick } from './panoply-paladin.ts';
import { apronHurt, artificerDue } from './panoply-artificer.ts';
import { mageDue } from './panoply-mage.ts';

/**
 * The panoply's pieces in a fight (docs/PANOPLIA.md §10): one per skill, each changing how its skill works. The skills'
 * programs call these where the piece changes them (`special` steps with `when: { uq }`); what happens later goes in a
 * queue (`s.uqDue`) run at the start of the next ticks, never inside the blow that made it (§8: jumps, echoes and ends
 * resolve in order and with caps). What is counted for each piece's feats is counted here too, in one line each.
 */


/** The most a tick runs of the queue, and the most jumps one hero makes in a tick (docs/PANOPLIA.md §8). */
const DUE_PER_TICK = 64;
const JUMPS_PER_HERO = 16;
/** What counts as a jump against that cap: the marks', and what runs from one to the next (mines, bolts, darts, chains, deaths). */
const JUMPS: ReadonlySet<UqDue['k']> = new Set<UqDue['k']>(['markJump', 'mineChain', 'boltBack', 'boltSplit', 'dartAgain', 'shareDeath', 'chainSeek']);

export const heroById = (s: BattleState, id: number): Hero | undefined => s.heroes.find((h) => h.id === id);

/** Puts a later effect in the queue (kept in tick order, then as it came: the queue stays deterministic). */
export const due = (s: BattleState, d: UqDue): void => {
  const q = (s.uqDue ??= []);
  let i = q.length;
  while (i > 0 && (q[i - 1] as UqDue).at > d.at) i--;
  q.splice(i, 0, d);
};

/** What a piece's control does on the big ones (§8): stun as the saga's pieces stun, never the bosses nor the dragon. */
export const pieceStun = (e: Enemy, t: number): number => (e.kind === 'dragon' ? 0 : heavyStun(e, t));

/** Moves a hero to a point at once (a leap), when there is ground there. */
const leapTo = (s: BattleState, h: Hero, x: number, y: number): boolean => {
  if (!heroWalkable(s, cellAtWorld(s, x, y))) return false;
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 400, kind: 'uqLeapFrom' });
  h.x = x;
  h.y = y;
  h.destX = x;
  h.destY = y;
  h.ax = x;
  h.ay = y;
  h.path = [];
  h.moving = false;
  ev(s, { t: 'boom', x, y, r: 600, kind: 'uqLeapTo' });
  return true;
};

/* ---------- Cuchilla del matarife (Golpe bajo) ---------- */

/** The low blow's mark carries its owner and how many jumps it has left (the butcher's cleaver). */
export const lowBlowMarked = (h: Hero, e: Enemy): void => {
  if (!hasUq(h, 'butcherCleaver')) return;
  e.markBy = h.id;
  e.markJumps = fx('butcherCleaver', h).jumps as number;
  e.markHop = 0;
};

/** A nightmare died: what the pieces do then (the barbarian's and the paladin's), and the cleaver's mark. */
export const panoplyDied = (s: BattleState, e: Enemy): void => {
  barbarianDied(s, e);
  paladinDied(s, e);
  cleaverDied(s, e);
};

/** A cleaver's mark on it jumps (next tick); the third of a chain fallen is the piece's test. */
const cleaverDied = (s: BattleState, e: Enemy): void => {
  if (e.markBy === undefined || !(e.markTicks && e.markTicks > 0)) return;
  const h = heroById(s, e.markBy);
  if (!h) return;
  if ((e.markJumps ?? 0) > 0) due(s, { at: s.tick + 1, k: 'markJump', hid: h.id, x: e.x, y: e.y, n: [e.markTicks, e.markBp ?? 10000, (e.markJumps ?? 0) - 1, (e.markHop ?? 0) + 1] });
  else if ((e.markHop ?? 0) >= 2) featPieceTest(s, h, 'butcherCleaver');
};

const markJump = (s: BattleState, h: Hero, d: UqDue): void => {
  const [ticks = 0, bp = 10000, jumps = 0, hop = 1] = d.n ?? [];
  const range = fx('butcherCleaver', h).range as number;
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const o of s.enemies) {
    if (o.hp <= 0 || !targetable(s, o) || o.kind === 'boneWall' || !withinRange(o.x, o.y, d.x, d.y, range)) continue;
    const dd = dist2(o.x, o.y, d.x, d.y);
    if (dd < bd || (dd === bd && best && o.id < best.id)) {
      bd = dd;
      best = o;
    }
  }
  if (!best) return;
  if (!best.markTicks || best.markTicks <= 0 || (best.markBp ?? 0) <= bp) {
    best.markBp = bp;
    best.markTicks = ticks;
  }
  best.markBy = h.id;
  best.markJumps = jumps;
  best.markHop = hop;
  ev(s, { t: 'beam', x: d.x, y: d.y, tx: best.x, ty: best.y, kind: 'markJump' });
  featUse(s, h, 'butcherCleaver', 1);
};

/** The cleaver's final form: the first basic attack of each hero on an exposed one is a critical. */
export const noGuardCrit = (s: BattleState, h: Hero, e: Enemy): boolean => {
  if (e.markBy === undefined || !(e.markTicks && e.markTicks > 0)) return false;
  const owner = heroById(s, e.markBy);
  if (!owner || !formFinal(owner, 'butcherCleaver')) return false;
  const seen = (e.guardOff ??= []);
  if (seen.includes(h.id)) return false;
  seen.push(h.id);
  return true;
};

/* ---------- Maza del cuartel (Embate) ---------- */

/**
 * What the slam pushed knocks into the nightmare behind it: both stunned (a piece's stun), both hit `damageBp` of the
 * blow. `hits` is everything the slam caught (nothing in it is knocked into twice); returns how many were stunned.
 */
export const slamClash = (s: BattleState, h: Hero, hits: Enemy[], dmg: number, clashed: Set<number>): number => {
  if (!hasUq(h, 'barracksMace')) return 0;
  const f = fx('barracksMace', h);
  let n = 0;
  for (const o of hits) {
    if (o.hp <= 0 || clashed.has(o.id)) continue;
    // The one behind: the nearest within reach, farther from the warrior than the pushed one, not caught by the slam.
    const reach = dist2(o.x, o.y, h.x, h.y);
    let behind: Enemy | null = null;
    let bd = 0x7fffffff;
    for (const b of s.enemies) {
      if (b.hp <= 0 || b.id === o.id || clashed.has(b.id) || hits.includes(b) || !targetable(s, b) || b.kind === 'boneWall') continue;
      if (!withinRange(b.x, b.y, o.x, o.y, f.behind as number) || dist2(b.x, b.y, h.x, h.y) <= reach) continue;
      const dd = dist2(b.x, b.y, o.x, o.y);
      if (dd < bd || (dd === bd && behind && b.id < behind.id)) {
        bd = dd;
        behind = b;
      }
    }
    if (!behind) continue;
    for (const e of [o, behind]) {
      clashed.add(e.id);
      const st = pieceStun(e, f.stun as number);
      if (st > e.stun) e.stun = st;
      if (st > 0) n++;
      damageEnemy(s, e, mulBp(dmg, f.damageBp as number), { pid: h.pid });
    }
    ev(s, { t: 'boom', x: idiv(o.x + behind.x, 2), y: idiv(o.y + behind.y, 2), r: 500, kind: 'maceClash' });
  }
  featUse(s, h, 'barracksMace', n);
  if (clashed.size >= FEAT.need.clash) featPieceTest(s, h, 'barracksMace');
  return n;
};

/** The mace's final form: the slam goes again a moment later, at its aim, `damageBp` as hard. */
export const slamAgainLater = (s: BattleState, h: Hero, ax: number, ay: number, r: number, deg: number, dmg: number, stun: number, push: number): void => {
  if (!formFinal(h, 'barracksMace')) return;
  const f = formFx('barracksMace', 'final');
  due(s, { at: s.tick + (f.after as number), k: 'slamAgain', hid: h.id, x: ax, y: ay, n: [r, deg, mulBp(dmg, f.damageBp as number), stun, push] });
};

const slamAgain = (s: BattleState, h: Hero, d: UqDue): void => {
  const [r = 0, deg = 0, dmg = 0, stun = 0, push = 0] = d.n ?? [];
  const hits = enemiesInCone(s, h, d.x, d.y, r, deg);
  for (const o of hits) {
    damageEnemy(s, o, dmg, { pid: h.pid });
    const st = stunFor(o, stun);
    if (st > o.stun) o.stun = st;
    shove(s, o, h.x, h.y, push);
  }
  ev(s, { t: 'boom', x: h.x, y: h.y, r, kind: 'shieldSlam2' });
  slamClash(s, h, hits, dmg, new Set());
};

/* ---------- Garfio del pozo (Cadena) ---------- */

/**
 * The chain caught something that doesn't move (an elite, a boss): the warrior leaps to it instead, hits it and stuns
 * it (a piece's stun). With Tirón (its final form) an elite and the warrior meet halfway; a boss, never moved. Returns
 * whether it took over the pull (once a cast: with two chains, the first).
 */
export const hookLeap = (s: BattleState, h: Hero, o: Enemy, dmg: number, gap: number, done: { leapt: boolean }): boolean => {
  if (!hasUq(h, 'wellHook') || done.leapt || o.kind === 'boneWall' || stunFor(o, 1) > 0 || o.kind === 'dragon' || isFlying(o)) return false;
  const f = fx('wellHook', h);
  const haul = formFinal(h, 'wellHook') && o.elite && !bigOne(o);
  if (haul) {
    // Halfway each: the elite dragged, the warrior leaping.
    const mid = { x: idiv(h.x + o.x, 2), y: idiv(h.y + o.y, 2) };
    const to = along(h.x, h.y, mid.x, mid.y, imax(0, isqrt(dist2(h.x, h.y, mid.x, mid.y)) - idiv(gap, 2)));
    if (heroWalkable(s, cellAtWorld(s, mid.x, mid.y))) {
      o.x = mid.x;
      o.y = mid.y;
      o.next = -1;
    }
    if (!leapTo(s, h, to.x, to.y)) return false;
  } else {
    const to = along(o.x, o.y, h.x, h.y, gap);
    if (!leapTo(s, h, to.x, to.y)) return false;
  }
  done.leapt = true;
  damageEnemy(s, o, mulBp(dmg, f.damageBp as number), { pid: h.pid });
  const st = pieceStun(o, f.stun as number);
  if (st > o.stun) o.stun = st;
  featUse(s, h, 'wellHook', 1);
  if (o.elite) featPieceWatch(s, h, 'wellHook', [o.id], FEAT.watch.hook, 1);
  return true;
};

/** Bosses, never moved by a piece (docs/UNICAS.md §3 «Qué es un jefe»: the big ones that nothing stuns). */
export const bigOne = (o: Enemy): boolean => stunFor({ ...o, elite: false }, 3) === 0;

/* ---------- Peto del patio de armas (Señuelo) ---------- */

/**
 * The decoy stands where the warrior was and the warrior lands on the point picked: what was around it stays with the
 * decoy. Counts the nightmares left at it; with Firme (its final form), the warrior takes half a moment.
 */
export const decoySwap = (s: BattleState, h: Hero, px: number, py: number, taunt: number): void => {
  if (!hasUq(h, 'drillPlate')) return;
  const decoy = (s.bots ?? []).filter((b) => b.kind === 'decoy' && b.owner === h.id).sort((a, b) => b.id - a.id)[0];
  if (!decoy) return;
  const fromX = h.x;
  const fromY = h.y;
  if (!leapTo(s, h, px, py)) return;
  decoy.x = fromX;
  decoy.y = fromY;
  decoy.hp = mulBp(decoy.hp, fx('drillPlate', h).hpBp as number);
  decoy.maxHp = decoy.hp;
  const left = enemiesNear(s, fromX, fromY, taunt, (o) => o.hp > 0 && targetable(s, o) && o.kind !== 'boneWall');
  featUse(s, h, 'drillPlate', left.length);
  if (left.length >= FEAT.need.decoy) featPieceTest(s, h, 'drillPlate');
  if (formFinal(h, 'drillPlate')) {
    const f = formFx('drillPlate', 'final');
    if (!(h.ward && h.ward > 0) || (h.wardBp ?? 10000) > (f.takenBp as number)) {
      h.ward = f.ticks as number;
      h.wardBp = f.takenBp as number;
    }
  }
  ev(s, { t: 'beam', x: fromX, y: fromY, tx: h.x, ty: h.y, kind: 'decoySwap' });
};

/* ---------- Coraza de la primera fila (Muro de escudos) ---------- */

/** The wall goes up: while it holds, it pushes what is pressed on the warrior (each `every` ticks). */
export const wallPushOn = (h: Hero, ticks: number): void => {
  if (hasUq(h, 'frontRankCuirass')) h.wallPush = ticks;
};

const wallPushTick = (s: BattleState, h: Hero): void => {
  if (!h.wallPush || h.wallPush <= 0) return;
  h.wallPush--;
  const f = fx('frontRankCuirass', h);
  if (h.wallPush % (f.every as number) !== 0) return;
  const fin = formFinal(h, 'frontRankCuirass');
  const ff = fin ? formFx('frontRankCuirass', 'final') : null;
  const reach = ff ? (ff.reach as number) : (f.reach as number);
  const dmg = mulBp(h.stats.attack, f.damageBp as number);
  let moved = 0;
  for (const o of enemiesNear(s, h.x, h.y, reach, (e) => e.hp > 0 && targetable(s, e) && e.kind !== 'boneWall')) {
    const x0 = o.x;
    const y0 = o.y;
    shove(s, o, h.x, h.y, f.push as number);
    damageEnemy(s, o, dmg, { pid: h.pid });
    if (o.x !== x0 || o.y !== y0) {
      moved++;
      if (ff && o.slowBp < (ff.slowBp as number)) o.slowBp = ff.slowBp as number;
      if (ff && o.slowTicks < (ff.ticks as number)) o.slowTicks = ff.ticks as number;
    }
  }
  if (moved) ev(s, { t: 'boom', x: h.x, y: h.y, r: reach, kind: 'wallPush' });
  featUse(s, h, 'frontRankCuirass', moved);
  if (moved >= FEAT.need.push) featPieceTest(s, h, 'frontRankCuirass');
};

/* ---------- Coraza del pregonero (Grito de reto) ---------- */

/** The shout: while it lasts, each challenged nightmare that strikes the warrior shields it; its end (Que tiemblen). */
export const courageOn = (s: BattleState, h: Hero, ticks: number, taunted: number): void => {
  if (!hasUq(h, 'criersCuirass')) return;
  h.courage = ticks;
  h.courageGot = 0;
  if (taunted >= FEAT.need.shout) featPieceWatch(s, h, 'criersCuirass', [h.id], ticks, -1);
  due(s, { at: s.tick + ticks, k: 'courageEnd', hid: h.id, x: h.x, y: h.y });
};

/** A blow on a hero: the crier's courage shields it (escudos: the stronger stays), the veteran's medallion gathers it. */
export const panoplyHurt = (s: BattleState, h: Hero, dmg: number, from: Enemy | null | undefined): void => {
  if (h.apronT) apronHurt(s, h, dmg);
  if (from && h.courage && h.courage > 0 && from.taunter === h.id && from.tauntTicks > 0) {
    const f = fx('criersCuirass', h);
    const cap = mulBp(h.stats.maxHp, f.capBp as number);
    const gain = imin(mulBp(h.stats.maxHp, f.perHitBp as number), cap - (h.courageGot ?? 0));
    if (gain > 0) {
      h.courageGot = (h.courageGot ?? 0) + gain;
      raiseShield(h, h.courageGot, h.courage + 24, false);
      featUse(s, h, 'criersCuirass', gain);
    }
  }
  if (h.uqT) clericHurt(s, h);
  if (h.standT && h.standT > 0) {
    const cap = mulBp(h.stats.maxHp, fx('veteranMedallion', h).capBp as number);
    const add = imin(dmg, cap - (h.standTaken ?? 0));
    if (add > 0) {
      h.standTaken = (h.standTaken ?? 0) + add;
      featUse(s, h, 'veteranMedallion', add);
    }
  }
};

const courageEnd = (s: BattleState, h: Hero): void => {
  h.courage = 0;
  if (!formFinal(h, 'criersCuirass')) return;
  const f = formFx('criersCuirass', 'final');
  for (const o of s.enemies) {
    if (o.hp <= 0 || o.taunter !== h.id) continue;
    // Pegan menos (§8): the big ones and the elites, at half of it.
    const bp = o.elite || bigOne(o) ? 10000 - idiv(10000 - (f.bp as number), 2) : (f.bp as number);
    if ((o.blind ?? 0) < (f.ticks as number)) o.blind = f.ticks as number;
    o.blindBp = imin(o.blindBp ?? 10000, bp);
  }
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 1500, kind: 'quail' });
};

/* ---------- Dedal del remendón (Estandarte de la guardia) ---------- */

/**
 * Where the thrown banner lands (docs/PANOPLIA.md §10, by itself for everyone): on the biggest pack within `range` (the
 * nightmare with the most others within the landing's `radius`; the nearest on a tie, then the lowest id), or where the
 * warrior stands if there is none.
 */
export const bannerSpot = (s: BattleState, h: Hero): { x: number; y: number } => {
  const f = fx('menderThimble', h);
  const near = enemiesNear(s, h.x, h.y, f.range as number, (o) => o.hp > 0 && targetable(s, o) && o.kind !== 'boneWall');
  let best: Enemy | null = null;
  let bn = 0;
  for (const o of near) {
    let n = 0;
    for (const b of near) if (withinRange(b.x, b.y, o.x, o.y, f.radius as number)) n++;
    if (n > bn || (n === bn && best && dist2(o.x, o.y, h.x, h.y) < dist2(best.x, best.y, h.x, h.y))) {
      bn = n;
      best = o;
    }
  }
  return best && heroWalkable(s, cellAtWorld(s, best.x, best.y)) ? { x: best.x, y: best.y } : { x: h.x, y: h.y };
};

/** The banner lands: it strikes and stuns around (a piece's stun). */
export const bannerLands = (s: BattleState, h: Hero, x: number, y: number): void => {
  const f = fx('menderThimble', h);
  let n = 0;
  for (const o of enemiesNear(s, x, y, f.radius as number, (e) => e.hp > 0 && targetable(s, e) && e.kind !== 'boneWall')) {
    damageEnemy(s, o, f.damage as number, { pid: h.pid });
    const st = pieceStun(o, f.stun as number);
    if (st > o.stun) o.stun = st;
    if (st > 0) n++;
  }
  ev(s, { t: 'beam', x: h.x, y: h.y, tx: x, ty: y, kind: 'bannerThrow' });
  ev(s, { t: 'boom', x, y, r: f.radius as number, kind: 'bannerLand' });
  featUse(s, h, 'menderThimble', n);
  if (n >= FEAT.need.banner) featPieceTest(s, h, 'menderThimble');
};

/* ---------- Clavo de la campana (Eco del escudo) ---------- */

/** The ring comes back: the echoes go in the queue; `elites` are the elites the first ring reached. */
export const echoAgainLater = (s: BattleState, h: Hero, radius: number, stun: number, dmg: number, elites: number[]): void => {
  if (!hasUq(h, 'bellNail')) return;
  const f = fx('bellNail', h);
  for (let i = 1; i <= (f.again as number); i++) due(s, { at: s.tick + i * (f.every as number), k: 'echoAgain', hid: h.id, x: h.x, y: h.y, n: [i, radius, stun, dmg], ids: elites });
  rallyCall(s, h, h.x, h.y, radius);
};

const echoAgain = (s: BattleState, h: Hero, d: UqDue): void => {
  const f = fx('bellNail', h);
  const [i = 1, radius = 0, stun = 0, dmg = 0] = d.n ?? [];
  const r = mulBp(radius, f.radiusBp as number);
  const reached: number[] = [];
  let n = 0;
  for (const o of enemiesNear(s, d.x, d.y, r, (e) => e.hp > 0 && targetable(s, e))) {
    damageEnemy(s, o, mulBp(dmg, f.damageBp as number), { pid: h.pid });
    const st = stunFor(o, mulBp(stun, f.stunBp as number));
    if (st > o.stun) o.stun = st;
    if (st > 0) n++;
    if (o.elite) reached.push(o.id);
  }
  ev(s, { t: 'boom', x: d.x, y: d.y, r, kind: 'shieldEchoAgain' });
  featUse(s, h, 'bellNail', n);
  // Every ring reaching the same elite: the next one follows those still reached.
  const still = (d.ids ?? []).filter((id) => reached.includes(id));
  if (i >= (f.again as number) && still.length) featPieceTest(s, h, 'bellNail');
  for (const q of s.uqDue ?? []) if (q.k === 'echoAgain' && q.hid === h.id && q.x === d.x && q.y === d.y && (q.n?.[0] ?? 0) === i + 1) q.ids = still;
  rallyCall(s, h, d.x, d.y, r);
};

/** Toque de reunión (the nail's final form): each ring mends the allies it reaches. */
const rallyCall = (s: BattleState, h: Hero, x: number, y: number, r: number): void => {
  if (!formFinal(h, 'bellNail')) return;
  const bp = formFx('bellNail', 'final').healBp as number;
  for (const o of s.heroes) if (o.downTicks === 0 && withinRange(o.x, o.y, x, y, r)) healHero(s, o, mulBp(o.stats.maxHp, bp), h.pid);
};

/* ---------- Medallón del veterano (Última línea) ---------- */

/** The last stand: what the warrior takes while it can't fall is gathered, and it all comes out at the end. */
export const standGather = (s: BattleState, h: Hero, ticks: number): void => {
  if (!hasUq(h, 'veteranMedallion')) return;
  h.standT = ticks;
  h.standTaken = 0;
  due(s, { at: s.tick + ticks, k: 'standEnd', hid: h.id, x: h.x, y: h.y });
};

const standEnd = (s: BattleState, h: Hero): void => {
  h.standT = 0;
  const taken = h.standTaken ?? 0;
  h.standTaken = 0;
  if (taken <= 0 || h.downTicks > 0) return;
  const f = fx('veteranMedallion', h);
  const low = h.hp * 10000 < h.stats.maxHp * FEAT.need.standLowBp;
  const healed = healHero(s, h, mulBp(taken, f.healBp as number), h.pid);
  const burst = mulBp(taken, f.burstBp as number);
  const stun = formFinal(h, 'veteranMedallion') ? (formFx('veteranMedallion', 'final').stun as number) : 0;
  for (const o of enemiesNear(s, h.x, h.y, f.radius as number, (e) => e.hp > 0 && targetable(s, e))) {
    damageEnemy(s, o, burst, { pid: h.pid });
    if (stun) {
      const st = pieceStun(o, stun);
      if (st > o.stun) o.stun = st;
    }
  }
  ev(s, { t: 'boom', x: h.x, y: h.y, r: f.radius as number, kind: 'standBurst' });
  if (low && healed * 10000 >= h.stats.maxHp * FEAT.need.standHealBp) featPieceTest(s, h, 'veteranMedallion');
};

/* ---------- each blow ---------- */

/** A hero's basic blow landed (heroHitExtras; `heal`: what an atonement in it mended, −1 none). */
export const panoplyHit = (s: BattleState, h: Hero, e: Enemy, dealt: number, heal: number): void => {
  if (h.cls === 'barbarian') barbarianHit(s, h, e);
  paladinHit(s, h, e, dealt, heal);
};

/* ---------- each tick ---------- */

/** Every tick, for a standing hero wearing a panoply piece: the wall's push, the timers. */
export const panoplyHeroTick = (s: BattleState, h: Hero): void => {
  if (h.cls === 'paladin') paladinTick(s, h);
  if (h.courage && h.courage > 0) h.courage--;
  if (h.standT && h.standT > 0) h.standT--;
  if (h.wallPush) wallPushTick(s, h);
  if (h.uqT) {
    rangerHeroTick(s, h);
    clericHeroTick(s, h);
  }
};

/** What the pieces left for this tick, in order, with a cap a tick (the rest waits). */
export const panoplyDue = (s: BattleState): void => {
  const q = s.uqDue;
  if (!q?.length) return;
  let ran = 0;
  const jumps = new Map<number, number>();
  while (q.length && (q[0] as UqDue).at <= s.tick && ran < DUE_PER_TICK) {
    const d = q.shift() as UqDue;
    ran++;
    const h = heroById(s, d.hid);
    if (!h) continue;
    if (JUMPS.has(d.k)) {
      const n = (jumps.get(h.id) ?? 0) + 1;
      jumps.set(h.id, n);
      if (n > JUMPS_PER_HERO) {
        due(s, { ...d, at: s.tick + 1 });
        continue;
      }
      if (d.k === 'markJump') markJump(s, h, d);
      else if (!artificerDue(s, h, d)) mageDue(s, h, d);
    } else if (druidDue(s, h, d)) continue;
    else if (h.downTicks > 0) continue;
    else if (d.k === 'slamAgain') slamAgain(s, h, d);
    else if (d.k === 'echoAgain') echoAgain(s, h, d);
    else if (d.k === 'standEnd') standEnd(s, h);
    else if (d.k === 'courageEnd') courageEnd(s, h);
    else if (barbarianDue(s, h, d) || paladinDue(s, h, d)) continue;
    else if (rangerDue(s, h, d) || clericDue(s, h, d)) continue;
    else if (!artificerDue(s, h, d)) mageDue(s, h, d);
  }
  if (!q.length) delete s.uqDue;
};
